'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui'
import { Button } from '@/components/ui'
import { AlertTriangle, X } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { shouldShowExpiryWarning, getCacheMetadata } from '@/utils/pwaCacheManager'

export default function SubscriptionExpiryWarning() {
  const router = useRouter()
  const [showWarning, setShowWarning] = useState(false)
  const [daysRemaining, setDaysRemaining] = useState(0)

  useEffect(() => {
    // 檢查是否需要顯示提醒
    if (shouldShowExpiryWarning()) {
      const metadata = getCacheMetadata()
      if (metadata) {
        const now = Date.now()
        // 使用與 shouldShowExpiryWarning 相同的計算方式（向下取整）
        const remaining = Math.floor((metadata.subscriptionEndDate - now) / (24 * 60 * 60 * 1000))
        setDaysRemaining(remaining)
        setShowWarning(true)
      }
    }
  }, [])

  const handleDismiss = () => {
    setShowWarning(false)
    // 記錄今天已顯示過提醒（避免同一天重複顯示）
    const today = new Date()
    const todayKey = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`
    localStorage.setItem('subscription_expiry_warning_shown_date', todayKey)
  }

  const handleRenew = () => {
    router.push('/subscribe')
  }

  // 檢查今天是否已經顯示過（在 shouldShowExpiryWarning 中已檢查，這裡不需要重複）
  if (!showWarning) {
    return null
  }

  return (
    <div className="fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-md">
      <Card className="border-orange-500 bg-orange-900/20">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center mb-2">
                <AlertTriangle className="h-5 w-5 text-orange-400 mr-2" />
                <h3 className="text-white font-semibold">學習權限即將到期</h3>
              </div>
              <p className="text-gray-300 text-sm mb-3">
                {daysRemaining === 0 ? (
                  <>
                    您的會員<span className="font-bold text-red-400">今天到期</span>！
                    <span className="block mt-1 text-red-300">
                      請立即續訂以繼續學習，否則學習進展將在明日自動清除。
                    </span>
                  </>
                ) : daysRemaining === 1 ? (
                  <>
                    您的會員將在 <span className="font-bold text-red-400">明天</span>到期
                    <span className="block mt-1 text-orange-300">
                      請盡快續訂以繼續學習，否則學習進展將在到期後清除。
                    </span>
                  </>
                ) : daysRemaining === 2 ? (
                  <>
                    您的會員將在 <span className="font-bold text-orange-400">2 天後</span>到期
                    <span className="block mt-1">
                      建議您提前續訂，以確保學習進度不中斷。
                    </span>
                  </>
                ) : daysRemaining === 3 ? (
                  <>
                    您的會員將在 <span className="font-bold text-orange-400">3 天後</span>到期
                    <span className="block mt-1">
                      建議您提前續訂，以確保學習進度不中斷。
                    </span>
                  </>
                ) : (
                  <>
                    您的會員將在 <span className="font-bold text-orange-400">{daysRemaining} 天</span>後到期
                  </>
                )}
              </p>
              <div className="flex space-x-2">
                <Button
                  onClick={handleRenew}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  立即續訂
                </Button>
                <Button
                  onClick={handleDismiss}
                  variant="secondary"
                  className="px-4 py-2 rounded-lg text-sm font-medium"
                >
                  稍後提醒
                </Button>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-white ml-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

